import { todayMx } from '../lib/dateUtils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  Plus, 
  Search, 
  Edit,
  Trash2,
  Loader2,
  X,
  User,
  Phone,
  MapPin,
  Calendar,
  ClipboardList,
  DollarSign,
  Wrench,
  Users,
  FileText,
  Receipt,
  ChevronDown,
  MessageSquare,
  CheckCircle2,
  Clock,
  Sparkles
} from 'lucide-react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';

const statusLabels = {
  pendiente: 'Pendiente',
  confirmado: 'Confirmado',
  en_proceso: 'En Proceso',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
  reagendado: 'Reagendado',
  presupuesto_pendiente: 'Presup. Pendiente',
  presupuesto_aprobado: 'Presup. Aprobado',
  // Estados de presupuestos
  en_revision: 'En Revisión',
  enviado: 'Enviado',
  aprobado: 'Aprobado',
  rechazado: 'Rechazado',
  sin_respuesta: 'Sin Respuesta',
  agendado: 'Agendado'
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  const [saving, setSaving] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [filterTech, setFilterTech] = useState('');
  const [historyForm, setHistoryForm] = useState(getEmptyHistory());

  // AI paste
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiParsing, setAiParsing] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  function getEmptyForm() {
    return { full_name: '', phones: [''], addresses: [{ street: '', reference: '' }], comments: '' };
  }

  function getEmptyHistory() {
    return { tipo_servicio: '', fecha: todayMx(), tecnico: '', monto_cobrado: '', folio_nota: '', forma_pago: '', comentarios: '' };
  }

  const serviceTypes = ['Sondeo de drenaje','Plomería','Electricidad','Aire Acondicionado','Detección de fugas','Mantenimiento general','Venta de minisplit','Otro'];
  const formasPago = ['Efectivo','Transferencia','Tarjeta','Cheque','Otro'];

  const fetchClients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterTech) params.append('technician', filterTech);
      const res = await axios.get(`${API}/clients?${params}`);
      setClients(res.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  }, [search, filterTech]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  useEffect(() => {
    axios.get(`${API}/technicians`).then(r => setTechnicians(r.data.filter(t => t.is_active !== false))).catch(() => {});
  }, []);

  const openNewDialog = () => {
    setSelectedClient(null);
    setFormData(getEmptyForm());
    setDialogOpen(true);
  };

  const openEditDialog = (client) => {
    setSelectedClient(client);
    setFormData({
      full_name: client.full_name,
      phones: client.phones?.length ? client.phones : [''],
      addresses: client.addresses?.length ? client.addresses : [{ street: '', reference: '' }],
      comments: client.comments || ''
    });
    setDialogOpen(true);
  };

  const openDetailDialog = async (client) => {
    try {
      const res = await axios.get(`${API}/clients/${client.id}`);
      setSelectedClient(res.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const handlePhoneChange = (index, value) => {
    const newPhones = [...formData.phones];
    newPhones[index] = value;
    setFormData({ ...formData, phones: newPhones });
  };

  const addPhone = () => {
    setFormData({ ...formData, phones: [...formData.phones, ''] });
  };

  const removePhone = (index) => {
    const newPhones = formData.phones.filter((_, i) => i !== index);
    setFormData({ ...formData, phones: newPhones.length ? newPhones : [''] });
  };

  const handleAddressChange = (index, field, value) => {
    const newAddresses = [...formData.addresses];
    newAddresses[index] = { ...newAddresses[index], [field]: value };
    setFormData({ ...formData, addresses: newAddresses });
  };

  const addAddress = () => {
    setFormData({ ...formData, addresses: [...formData.addresses, { street: '', reference: '' }] });
  };

  const removeAddress = (index) => {
    const newAddresses = formData.addresses.filter((_, i) => i !== index);
    setFormData({ ...formData, addresses: newAddresses.length ? newAddresses : [{ street: '', reference: '' }] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        phones: formData.phones.filter(p => p.trim()),
        addresses: formData.addresses.filter(a => a.street.trim())
      };

      if (selectedClient) {
        await axios.put(`${API}/clients/${selectedClient.id}`, data);
        toast.success('Cliente actualizado');
        setDialogOpen(false);
      } else {
        const res = await axios.post(`${API}/clients`, data);
        toast.success('Cliente creado');
        setDialogOpen(false);
        // Auto-open "Agregar Trabajo" for the new client
        setSelectedClient(res.data);
        setHistoryForm(getEmptyHistory());
        setHistoryDialogOpen(true);
      }
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;
    try {
      await axios.delete(`${API}/clients/${selectedClient.id}`);
      toast.success('Cliente eliminado');
      setDeleteDialogOpen(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleAddHistory = async (e) => {
    e.preventDefault();
    if (!historyForm.tipo_servicio || !historyForm.fecha) {
      toast.error('Tipo de servicio y fecha son obligatorios');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/clients/${selectedClient.id}/history`, historyForm);
      toast.success('Trabajo registrado');
      setHistoryDialogOpen(false);
      // Refresh client detail
      const res = await axios.get(`${API}/clients/${selectedClient.id}`);
      setSelectedClient(res.data);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  // AI Parse
  const handleAiParse = async () => {
    if (!aiText.trim()) { toast.error('Pega o escribe el texto del cliente'); return; }
    setAiParsing(true);
    try {
      const res = await axios.post(`${API}/clients/parse-ai`, { text: aiText });
      if (res.data.error) { toast.error(res.data.error); return; }
      setAiResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar con IA');
    } finally { setAiParsing(false); }
  };

  const handleAiConfirm = async () => {
    if (!aiResult) return;
    setSaving(true);
    try {
      // Create client
      const clientData = {
        full_name: aiResult.nombre || 'Sin nombre',
        phones: aiResult.telefono ? [aiResult.telefono] : [],
        addresses: aiResult.direccion ? [{ street: aiResult.direccion, reference: aiResult.referencias || '' }] : [],
        comments: aiResult.comentarios_cliente || ''
      };
      const clientRes = await axios.post(`${API}/clients`, clientData);
      const newClient = clientRes.data;

      // Add work history if there's service data
      if (aiResult.tipo_servicio || aiResult.monto_cobrado || aiResult.fecha) {
        await axios.post(`${API}/clients/${newClient.id}/history`, {
          tipo_servicio: aiResult.tipo_servicio || 'Otro',
          fecha: aiResult.fecha || todayMx(),
          tecnico: aiResult.tecnico || '',
          monto_cobrado: aiResult.monto_cobrado || 0,
          folio_nota: aiResult.folio_nota || '',
          forma_pago: aiResult.forma_pago || '',
          comentarios: aiResult.comentarios_trabajo || ''
        });
      }

      toast.success(`Cliente "${aiResult.nombre}" creado con trabajo registrado`);
      setAiDialogOpen(false);
      setAiText('');
      setAiResult(null);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Cartera de Clientes</h2>
          <p className="text-muted-foreground text-sm">{clients.length} clientes registrados</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setAiText(''); setAiResult(null); setAiDialogOpen(true); }} data-testid="ai-paste-btn">
            <Sparkles className="h-4 w-4 mr-2" />
            Pegar con IA
          </Button>
          <Button onClick={openNewDialog} data-testid="new-client-button">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, teléfono, dirección o folio de nota..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="client-search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant={!filterTech ? 'default' : 'outline'} size="sm" className="h-8 text-xs rounded-full" onClick={() => setFilterTech('')}>
              Todos
            </Button>
            {technicians.map(t => (
              <Button key={t.id} variant={filterTech === t.name ? 'default' : 'outline'} size="sm" className="h-8 text-xs rounded-full" onClick={() => setFilterTech(filterTech === t.name ? '' : t.name)}>
                {t.name.split(' ')[0]}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Clients Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {clients.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No se encontraron clientes</h3>
              <p className="text-muted-foreground">
                {search ? 'Intenta con otro término de búsqueda' : 'Agrega tu primer cliente'}
              </p>
            </CardContent>
          </Card>
        ) : (
          clients.map((client) => (
            <Card 
              key={client.id} 
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => openDetailDialog(client)}
              data-testid={`client-card-${client.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{client.full_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {client.phones?.[0] || 'Sin teléfono'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditDialog(client);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedClient(client);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Servicios</span>
                    <Badge variant="secondary">{client.total_services || 0}</Badge>
                  </div>
                  {client.total_cobrado > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Total cobrado</span>
                      <span className="font-medium text-green-600">${(client.total_cobrado || 0).toLocaleString()}</span>
                    </div>
                  )}
                  {client.last_service_date && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Último servicio</span>
                      <span>{format(parseISO(client.last_service_date), 'dd/MM/yy')}</span>
                    </div>
                  )}
                  {client.ultimo_folio_nota && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Último folio</span>
                      <span className="font-mono text-xs text-blue-600">{client.ultimo_folio_nota}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Client Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedClient ? 'Editar Cliente' : 'Nuevo Cliente'}
            </DialogTitle>
            <DialogDescription>
              {selectedClient ? 'Modifica los datos del cliente' : 'Completa los datos para crear un nuevo cliente'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre Completo *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Nombre completo del cliente"
                required
                data-testid="client-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Teléfonos</Label>
              {formData.phones.map((phone, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={phone}
                    onChange={(e) => handlePhoneChange(index, e.target.value)}
                    placeholder="Número de teléfono"
                  />
                  {formData.phones.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removePhone(index)}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addPhone}>
                <Plus className="h-4 w-4 mr-2" />
                Agregar Teléfono
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Domicilios</Label>
              {formData.addresses.map((address, index) => (
                <div key={index} className="space-y-2 p-3 bg-muted/50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Domicilio {index + 1}</span>
                    {formData.addresses.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeAddress(index)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <Input
                    value={address.street}
                    onChange={(e) => handleAddressChange(index, 'street', e.target.value)}
                    placeholder="Dirección completa"
                  />
                  <Input
                    value={address.reference}
                    onChange={(e) => handleAddressChange(index, 'reference', e.target.value)}
                    placeholder="Referencias"
                  />
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addAddress}>
                <Plus className="h-4 w-4 mr-2" />
                Agregar Domicilio
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Comentarios Internos</Label>
              <Textarea
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                placeholder="Notas sobre el cliente"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-client-button">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  selectedClient ? 'Actualizar' : 'Crear Cliente'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Ficha del Cliente
            </DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{selectedClient.full_name}</h2>
                  <p className="text-muted-foreground">
                    Cliente desde {format(parseISO(selectedClient.created_at), 'MMMM yyyy', { locale: es })}
                  </p>
                </div>
              </div>

              {/* Resumen de estadísticas */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card>
                  <CardContent className="p-3 text-center">
                    <ClipboardList className="h-5 w-5 mx-auto text-primary mb-1" />
                    <p className="text-xl font-bold">{selectedClient.total_movimientos || selectedClient.total_services || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Atenciones</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <DollarSign className="h-5 w-5 mx-auto text-green-600 mb-1" />
                    <p className="text-xl font-bold text-green-600">${(selectedClient.total_cobrado || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total Cobrado</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <Receipt className="h-5 w-5 mx-auto text-blue-600 mb-1" />
                    <p className="text-xl font-bold text-blue-600">${(selectedClient.total_cotizado || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total Cotizado</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <Users className="h-5 w-5 mx-auto text-purple-600 mb-1" />
                    <p className="text-xl font-bold text-purple-600">{selectedClient.tecnicos_que_atendieron?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Técnicos</p>
                  </CardContent>
                </Card>
              </div>

              {/* Último técnico que atendió */}
              {selectedClient.ultimo_tecnico && (
                <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                  <p className="text-xs font-medium text-purple-800 dark:text-purple-400 mb-2 flex items-center gap-2">
                    <Wrench className="h-4 w-4" />
                    Último Técnico que Atendió
                  </p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold">{selectedClient.ultimo_tecnico.nombre}</p>
                      <p className="text-xs text-muted-foreground">
                        {selectedClient.ultimo_tecnico.servicio} - {selectedClient.ultimo_tecnico.fecha && format(parseISO(selectedClient.ultimo_tecnico.fecha), 'dd/MM/yyyy')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Técnicos que han atendido */}
              {selectedClient.tecnicos_que_atendieron?.length > 0 && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <p className="text-xs font-medium text-blue-800 dark:text-blue-400 mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Técnicos que han Atendido a este Cliente
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {selectedClient.tecnicos_que_atendieron.map((tecnico) => (
                      <div key={tecnico.id} className="flex items-center justify-between p-2 bg-white/50 dark:bg-black/20 rounded">
                        <span className="font-medium text-sm">{tecnico.nombre}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {tecnico.visitas} {tecnico.visitas === 1 ? 'visita' : 'visitas'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Último Servicio Completado */}
              {selectedClient.ultimo_folio && (
                <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg">
                  <p className="text-xs font-medium text-green-800 dark:text-green-400 mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Último Servicio Completado
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Folio de Nota</p>
                      <p className="font-mono font-medium">{selectedClient.ultimo_folio}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Monto Cobrado</p>
                      <p className="text-lg font-bold text-green-600 dark:text-green-400">
                        ${selectedClient.ultimo_monto_cobrado?.toLocaleString() || 0}
                      </p>
                    </div>
                    {selectedClient.ultimo_servicio_tipo && (
                      <div>
                        <p className="text-xs text-muted-foreground">Tipo de Servicio</p>
                        <p className="text-sm">{selectedClient.ultimo_servicio_tipo}</p>
                      </div>
                    )}
                    {selectedClient.ultimo_tecnico && (
                      <div>
                        <p className="text-xs text-muted-foreground">Técnico</p>
                        <p className="text-sm">{selectedClient.ultimo_tecnico.nombre}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Teléfonos</p>
                    <p className="text-muted-foreground">
                      {selectedClient.phones?.length 
                        ? selectedClient.phones.join(', ')
                        : 'Sin teléfono registrado'
                      }
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Domicilios</p>
                    {selectedClient.addresses?.length ? (
                      <div className="space-y-2 mt-1">
                        {selectedClient.addresses.map((addr, idx) => (
                          <div key={idx} className="text-sm">
                            <p>{addr.street}</p>
                            {addr.reference && (
                              <p className="text-xs text-muted-foreground">Ref: {addr.reference}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">Sin domicilio registrado</p>
                    )}
                  </div>
                </div>

                {selectedClient.comments && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-400">Comentarios</p>
                    <p className="text-sm mt-1">{selectedClient.comments}</p>
                  </div>
                )}

                {selectedClient.created_by && (
                  <div className="p-3 bg-slate-50 dark:bg-slate-950/30 rounded-lg">
                    <p className="text-xs font-medium text-slate-800 dark:text-slate-400">Registrado por</p>
                    <p className="text-sm mt-1 font-medium">{selectedClient.created_by}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(parseISO(selectedClient.created_at), "dd/MM/yyyy 'a las' HH:mm", { locale: es })}
                    </p>
                  </div>
                )}
              </div>

              {/* Historial Completo - Tarjetas Expandibles */}
              {selectedClient.historial?.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Historial Completo ({selectedClient.historial.length})
                    </h3>
                    <Button size="sm" variant="outline" onClick={() => { setHistoryForm(getEmptyHistory()); setHistoryDialogOpen(true); }} data-testid="add-history-btn">
                      <Plus className="h-4 w-4 mr-1" />Agregar Trabajo
                    </Button>
                  </div>
                  <ScrollArea className="h-96">
                    <div className="space-y-2 pr-3">
                      {selectedClient.historial.map((item) => (
                        <Collapsible key={item.id}>
                          <div className="border rounded-lg overflow-hidden">
                            <CollapsibleTrigger className="w-full">
                              <div className="p-3 hover:bg-muted/50 transition-colors">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="text-left">
                                      <div className="flex items-center gap-2">
                                        <Badge variant={item.tipo_movimiento === 'Servicio' ? 'default' : 'secondary'} className="text-xs">
                                          {item.tipo_movimiento}
                                        </Badge>
                                        <span className="font-mono text-xs text-muted-foreground">{item.folio}</span>
                                        {item.folio_nota && (
                                          <span className="font-mono text-xs text-green-600">({item.folio_nota})</span>
                                        )}
                                      </div>
                                      <p className="font-medium text-sm mt-1">{item.servicio_tipo}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="text-right">
                                      <p className="text-xs text-muted-foreground">
                                        {item.fecha ? format(parseISO(item.fecha), 'dd/MM/yyyy') : '-'}
                                      </p>
                                      <Badge className={`status-${item.estado} text-xs mt-1`}>
                                        {statusLabels[item.estado] || item.estado}
                                      </Badge>
                                    </div>
                                    <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform" />
                                  </div>
                                </div>
                              </div>
                            </CollapsibleTrigger>
                            
                            <CollapsibleContent>
                              <div className="px-3 pb-3 pt-0 border-t bg-muted/30 space-y-3">
                                {/* Info Principal */}
                                <div className="grid grid-cols-2 gap-3 pt-3">
                                  <div className="flex items-center gap-2 text-sm">
                                    <Wrench className="h-4 w-4 text-muted-foreground" />
                                    <span>{item.tecnico_nombre || 'Sin asignar'}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                                    <span>Cotizado: ${(item.monto_cotizado || 0).toLocaleString()}</span>
                                  </div>
                                </div>

                                {/* Pagos */}
                                {(item.monto_total > 0 || item.anticipo > 0 || item.monto_cobrado > 0) && (
                                  <div className="p-2 bg-background rounded-md">
                                    <p className="text-xs font-medium text-muted-foreground mb-2">Pagos</p>
                                    <div className="grid grid-cols-3 gap-2 text-xs">
                                      {item.monto_total > 0 && (
                                        <div>
                                          <span className="text-muted-foreground">Total:</span>
                                          <span className="ml-1 font-medium">${item.monto_total.toLocaleString()}</span>
                                        </div>
                                      )}
                                      {item.anticipo > 0 && (
                                        <div>
                                          <span className="text-muted-foreground">Anticipo:</span>
                                          <span className="ml-1 font-medium text-green-600">${item.anticipo.toLocaleString()}</span>
                                        </div>
                                      )}
                                      {item.restante_por_pagar > 0 && (
                                        <div>
                                          <span className="text-muted-foreground">Restante:</span>
                                          <span className="ml-1 font-medium text-orange-600">${item.restante_por_pagar.toLocaleString()}</span>
                                        </div>
                                      )}
                                      {item.monto_cobrado > 0 && (
                                        <div className="col-span-3">
                                          <span className="text-muted-foreground">Cobrado:</span>
                                          <span className="ml-1 font-medium text-green-600">${item.monto_cobrado.toLocaleString()}</span>
                                          {item.forma_pago && <span className="ml-2 text-muted-foreground">({item.forma_pago})</span>}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Descripción del trabajo */}
                                {item.descripcion && (
                                  <div className="p-2 bg-background rounded-md">
                                    <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                                      <ClipboardList className="h-3 w-3" />
                                      Descripción del trabajo
                                    </p>
                                    <p className="text-sm">{item.descripcion}</p>
                                  </div>
                                )}

                                {/* Comentarios internos */}
                                {item.comentarios && (
                                  <div className="p-2 bg-background rounded-md">
                                    <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                                      <MessageSquare className="h-3 w-3" />
                                      Comentarios internos
                                    </p>
                                    <p className="text-sm">{item.comentarios}</p>
                                  </div>
                                )}

                                {/* Comentario de cierre */}
                                {item.comentario_cierre && (
                                  <div className="p-2 bg-green-50 dark:bg-green-950/30 rounded-md border border-green-200 dark:border-green-800">
                                    <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-1 flex items-center gap-1">
                                      <CheckCircle2 className="h-3 w-3" />
                                      Comentario al finalizar
                                    </p>
                                    <p className="text-sm text-green-800 dark:text-green-300">{item.comentario_cierre}</p>
                                  </div>
                                )}

                                {/* Info de cierre */}
                                {item.estado === 'finalizado' && item.fecha_cierre && (
                                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      Finalizado: {format(parseISO(item.fecha_cierre), "dd/MM/yyyy 'a las' HH:mm", { locale: es })}
                                    </span>
                                    {item.cerrado_por && (
                                      <span>Por: {item.cerrado_por}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </CollapsibleContent>
                          </div>
                        </Collapsible>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              ) : (
                <div className="text-center py-6">
                  <ClipboardList className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground mb-3">Sin historial de servicios</p>
                  <Button size="sm" onClick={() => { setHistoryForm(getEmptyHistory()); setHistoryDialogOpen(true); }}>
                    <Plus className="h-4 w-4 mr-1" />Agregar Primer Trabajo
                  </Button>
                </div>
              )}

              {/* Fallback: Historial antiguo si no hay historial nuevo */}
              {!selectedClient.historial?.length && selectedClient.services?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Historial de Servicios</h3>
                  <ScrollArea className="h-64">
                    <div className="space-y-2">
                      {selectedClient.services.map((service) => (
                        <div key={service.id} className="p-3 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-sm">{service.service_type}</p>
                              <p className="text-xs text-muted-foreground">{service.folio}</p>
                            </div>
                            <Badge className={`status-${service.status}`}>
                              {statusLabels[service.status]}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(parseISO(service.service_date), 'dd/MM/yy')}
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign className="h-3 w-3" />
                              ${service.quoted_cost?.toLocaleString() || 0}
                            </span>
                            {service.technician_name && (
                              <span className="flex items-center gap-1">
                                <Wrench className="h-3 w-3" />
                                {service.technician_name}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registrar Trabajo Realizado</DialogTitle>
            <DialogDescription>{selectedClient?.full_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddHistory} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Servicio *</Label>
                <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={historyForm.tipo_servicio} onChange={(e) => setHistoryForm({...historyForm, tipo_servicio: e.target.value})}>
                  <option value="">Seleccionar...</option>
                  {serviceTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Fecha del Servicio *</Label>
                <Input type="date" value={historyForm.fecha} onChange={(e) => setHistoryForm({...historyForm, fecha: e.target.value})} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Técnico que Acudió</Label>
                <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={historyForm.tecnico} onChange={(e) => setHistoryForm({...historyForm, tecnico: e.target.value})}>
                  <option value="">Seleccionar...</option>
                  {technicians.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Monto Cobrado</Label>
                <Input type="number" value={historyForm.monto_cobrado} onChange={(e) => setHistoryForm({...historyForm, monto_cobrado: e.target.value})} placeholder="$0" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Folio de Nota</Label>
                <Input value={historyForm.folio_nota} onChange={(e) => setHistoryForm({...historyForm, folio_nota: e.target.value})} placeholder="Folio entregado" />
              </div>
              <div className="space-y-2">
                <Label>Forma de Pago</Label>
                <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={historyForm.forma_pago} onChange={(e) => setHistoryForm({...historyForm, forma_pago: e.target.value})}>
                  <option value="">Seleccionar...</option>
                  {formasPago.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Comentarios del Trabajo</Label>
              <Textarea value={historyForm.comentarios} onChange={(e) => setHistoryForm({...historyForm, comentarios: e.target.value})} placeholder="Detalles del trabajo realizado..." rows={3} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setHistoryDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : 'Registrar Trabajo'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Paste Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-blue-500" />Pegar datos con IA</DialogTitle>
            <DialogDescription>Pega el texto con los datos del cliente y trabajo. La IA los acomodará automáticamente.</DialogDescription>
          </DialogHeader>

          {!aiResult ? (
            <div className="space-y-4">
              <Textarea
                value={aiText}
                onChange={(e) => setAiText(e.target.value)}
                placeholder={"Ejemplo:\nCliente Juan Pérez, tel 686 123 4567, vive en Av. Reforma 123 col centro.\nSe le hizo sondeo de drenaje el 15 de marzo, se cobró $2,500 en efectivo.\nFue el técnico José, folio nota 045."}
                rows={8}
                className="text-sm"
                data-testid="ai-text-input"
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAiDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleAiParse} disabled={aiParsing || !aiText.trim()} data-testid="ai-parse-btn">
                  {aiParsing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Procesando...</> : <><Sparkles className="h-4 w-4 mr-2" />Procesar con IA</>}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-sm font-medium text-green-700 dark:text-green-400 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />Datos extraídos. Revisa y corrige si es necesario.</p>
              </div>

              <div className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Datos del Cliente</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Nombre</Label><Input value={aiResult.nombre || ''} onChange={(e) => setAiResult({...aiResult, nombre: e.target.value})} /></div>
                  <div className="space-y-1"><Label className="text-xs">Teléfono</Label><Input value={aiResult.telefono || ''} onChange={(e) => setAiResult({...aiResult, telefono: e.target.value})} /></div>
                </div>
                <div className="space-y-1"><Label className="text-xs">Dirección</Label><Input value={aiResult.direccion || ''} onChange={(e) => setAiResult({...aiResult, direccion: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">Referencias</Label><Input value={aiResult.referencias || ''} onChange={(e) => setAiResult({...aiResult, referencias: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">Comentarios del cliente</Label><Input value={aiResult.comentarios_cliente || ''} onChange={(e) => setAiResult({...aiResult, comentarios_cliente: e.target.value})} /></div>
              </div>

              <div className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Trabajo Realizado</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Tipo de servicio</Label>
                    <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={aiResult.tipo_servicio || ''} onChange={(e) => setAiResult({...aiResult, tipo_servicio: e.target.value})}>
                      <option value="">Seleccionar...</option>
                      {serviceTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Fecha</Label><Input type="date" value={aiResult.fecha || ''} onChange={(e) => setAiResult({...aiResult, fecha: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Técnico</Label>
                    <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={aiResult.tecnico || ''} onChange={(e) => setAiResult({...aiResult, tecnico: e.target.value})}>
                      <option value="">Seleccionar...</option>
                      {technicians.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Monto cobrado</Label><Input type="number" value={aiResult.monto_cobrado || ''} onChange={(e) => setAiResult({...aiResult, monto_cobrado: parseFloat(e.target.value) || 0})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Folio de nota</Label><Input value={aiResult.folio_nota || ''} onChange={(e) => setAiResult({...aiResult, folio_nota: e.target.value})} /></div>
                  <div className="space-y-1"><Label className="text-xs">Forma de pago</Label>
                    <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={aiResult.forma_pago || ''} onChange={(e) => setAiResult({...aiResult, forma_pago: e.target.value})}>
                      <option value="">Seleccionar...</option>
                      {formasPago.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                </div>
                <div className="space-y-1"><Label className="text-xs">Comentarios del trabajo</Label><Textarea value={aiResult.comentarios_trabajo || ''} onChange={(e) => setAiResult({...aiResult, comentarios_trabajo: e.target.value})} rows={2} /></div>
              </div>

              <DialogFooter className="gap-2">
                <Button type="button" variant="ghost" onClick={() => setAiResult(null)}>Volver a pegar</Button>
                <Button type="button" variant="outline" onClick={() => setAiDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleAiConfirm} disabled={saving} data-testid="ai-confirm-btn">
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Guardando...</> : 'Confirmar y guardar'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Cliente</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar a {selectedClient?.full_name}? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
